import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  TextInput,
  Modal,
  Platform,
  Alert,
  useColorScheme,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';

const ProfileScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  
  const [profileData, setProfileData] = useState({
    avatar: null,
    username: '用户昵称',
    gender: '未设置',
    age: '未设置',
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [tempData, setTempData] = useState({...profileData});
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showAvatarOptions, setShowAvatarOptions] = useState(false);
  
  // 打开图片选择器
  const handleAvatarChange = () => {
    setShowAvatarOptions(true);
  };

  // 拍照
  const takePhoto = async () => {
    setShowAvatarOptions(false);
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('需要相机权限', '请在设置中允许应用访问您的相机');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled) {
      processImage(result.assets[0].uri);
    }
  };

  // 从相册选择
  const pickImage = async () => {
    setShowAvatarOptions(false);
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('需要相册权限', '请在设置中允许应用访问您的相册');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled) {
      processImage(result.assets[0].uri);
    }
  };

  // 简单美化处理图片
  const processImage = async (uri) => {
    try {
      const manipResult = await ImageManipulator.manipulateAsync(
        uri,
        [
          { resize: { width: 400, height: 400 } },
          { crop: { originX: 0, originY: 0, width: 400, height: 400 } },
        ],
        { compress: 0.8, format: ImageManipulator.SaveFormat.JPEG }
      );
      
      setTempData({...tempData, avatar: manipResult.uri});
    } catch (error) {
      console.error('图片处理失败', error);
      Alert.alert('错误', '图片处理失败，请重试');
    }
  };

  // 开始编辑
  const startEditing = () => {
    setTempData({...profileData});
    setIsEditing(true);
  };

  // 保存编辑
  const saveChanges = () => {
    setProfileData({...tempData});
    setIsEditing(false);
  };

  // 取消编辑
  const cancelEditing = () => {
    setIsEditing(false);
  };

  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      <View style={{ alignItems: 'center', marginBottom: 24 }}>
        <View style={{ position: 'relative', marginBottom: 8 }}>
          <TouchableOpacity
            disabled={!isEditing}
            onPress={handleAvatarChange}
            style={{
              position: 'relative',
              borderRadius: 60,
              overflow: 'hidden',
              borderWidth: 2,
              borderColor: '#2563eb',
              width: 120,
              height: 120,
            }}
          >
            {tempData.avatar ? (
              <Image
                source={{ uri: tempData.avatar }}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <View style={{
                backgroundColor: '#cbd9f7',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}>
                <Ionicons name="person" size={60} color="#2563eb" />
              </View>
            )}
          </TouchableOpacity>
          
          {isEditing && (
            <View style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              backgroundColor: '#2563eb',
              borderRadius: 18,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="camera" size={20} color="#fff" />
            </View>
          )}
        </View>
        
        <Text style={{
          fontSize: 20,
          fontWeight: 'bold',
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          {profileData.username}
        </Text>
      </View>

      {/* 编辑模式下的操作按钮 */}
      {isEditing ? (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 24 }}>
          <TouchableOpacity
            onPress={cancelEditing}
            style={{
              flex: 1,
              paddingVertical: 12,
              backgroundColor: isDarkMode ? '#c2cada' : '#f3f4f6',
              borderRadius: 8,
              marginRight: 8,
            }}
          >
            <Text style={{
              textAlign: 'center',
              fontWeight: '600',
              color: isDarkMode ? '#1f2937' : '#4b5563',
            }}>
              取消
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={saveChanges}
            style={{
              flex: 1,
              paddingVertical: 12,
              backgroundColor: '#2563eb',
              borderRadius: 8,
              marginLeft: 8,
            }}
          >
            <Text style={{
              textAlign: 'center',
              fontWeight: '600',
              color: 'white',
            }}>
              保存
            </Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          onPress={startEditing}
          style={{
            paddingVertical: 12,
            marginBottom: 24,
            backgroundColor: '#2563eb',
            borderRadius: 8,
          }}
        >
          <Text style={{
            textAlign: 'center',
            fontWeight: '600',
            color: 'white',
          }}>
            编辑资料
          </Text>
        </TouchableOpacity>
      )}

      {/* 头像选择模态框 - Web 友好的替代方案 */}
      <Modal
        visible={showAvatarOptions}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowAvatarOptions(false)}
      >
        <View style={{
          flex: 1,
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
          <View style={{
            padding: 16,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              textAlign: 'center',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              更换头像
            </Text>
            
            <TouchableOpacity
              style={{
                paddingVertical: 14,
                borderBottomWidth: 1,
                borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
              }}
              onPress={takePhoto}
            >
              <Text style={{ 
                color: isDarkMode ? 'white' : '#1f2937',
                textAlign: 'center',
                fontSize: 16,
              }}>拍照</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{
                paddingVertical: 14,
                borderBottomWidth: 1,
                borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
              }}
              onPress={pickImage}
            >
              <Text style={{ 
                color: isDarkMode ? 'white' : '#1f2937',
                textAlign: 'center',
                fontSize: 16,
              }}>从相册选择</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{
                paddingVertical: 12,
                marginTop: 16,
                backgroundColor: isDarkMode ? '#657084' : '#e5e7eb',
                borderRadius: 8,
              }}
              onPress={() => setShowAvatarOptions(false)}
            >
              <Text style={{
                textAlign: 'center',
                fontWeight: '600',
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                取消
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* 基础信息 */}
      <View style={{
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          基本资料
        </Text>
        
        {/* 用户名 */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
        }}>
          <Text style={{
            color: isDarkMode ? '#e0e0e0' : '#4b5563',
          }}>
            用户名
          </Text>
          {isEditing ? (
            <TextInput
              value={tempData.username}
              onChangeText={(text) => setTempData({...tempData, username: text})}
              style={{
                textAlign: 'right',
                color: isDarkMode ? 'white' : '#1f2937',
                minWidth: 120,
              }}
              placeholder="设置用户名"
              placeholderTextColor={isDarkMode ? '#6b7280' : '#9ca3af'}
            />
          ) : (
            <Text style={{
              fontWeight: '500',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {profileData.username}
            </Text>
          )}
        </View>
        
        {/* 性别 */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
        }}>
          <Text style={{
            color: isDarkMode ? '#e0e0e0' : '#4b5563',
          }}>
            性别
          </Text>
          {isEditing ? (
            <TouchableOpacity
              onPress={() => setShowGenderModal(true)}
              style={{ flexDirection: 'row', alignItems: 'center' }}
            >
              <Text style={{
                marginRight: 8,
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {tempData.gender}
              </Text>
              <Ionicons name="chevron-forward" size={16} color={isDarkMode ? '#fff' : '#374151'} />
            </TouchableOpacity>
          ) : (
            <Text style={{
              fontWeight: '500',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {profileData.gender}
            </Text>
          )}
        </View>
        
        {/* 年龄 */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 12,
        }}>
          <Text style={{
            color: isDarkMode ? '#e0e0e0' : '#4b5563',
          }}>
            年龄
          </Text>
          {isEditing ? (
            <TextInput
              value={tempData.age}
              onChangeText={(text) => setTempData({...tempData, age: text})}
              style={{
                textAlign: 'right',
                color: isDarkMode ? 'white' : '#1f2937',
                minWidth: 120,
              }}
              placeholder="设置年龄"
              placeholderTextColor={isDarkMode ? '#6b7280' : '#9ca3af'}
              keyboardType="number-pad"
            />
          ) : (
            <Text style={{
              fontWeight: '500',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {profileData.age}
            </Text>
          )}
        </View>
      </View>

      {/* 健康数据管理 */}
      <TouchableOpacity
        onPress={() => navigation.navigate('HealthData')}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 16,
          marginBottom: 16,
          backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          borderRadius: 12,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 2,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: '#3b82f6',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}>
            <Ionicons name="fitness-outline" size={22} color="white" />
          </View>
          <View>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 4,
            }}>
              健康数据管理
            </Text>
            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#cbd5e1' : '#6b7280',
            }}>
              身高、体重记录及BMI指数计算
            </Text>
          </View>
        </View>
        <Ionicons name="chevron-forward" size={20} color={isDarkMode ? '#fff' : '#374151'} />
      </TouchableOpacity>

      {/* 目标管理 */}
      <TouchableOpacity
        onPress={() => navigation.navigate('Goal')}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 16,
          marginBottom: 16,
          backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          borderRadius: 12,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 2,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: '#10b981',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}>
            <Ionicons name="flag-outline" size={22} color="white" />
          </View>
          <View>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 4,
            }}>
              目标管理
            </Text>
            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#cbd5e1' : '#6b7280',
            }}>
              健康目标设定与成就系统
            </Text>
          </View>
        </View>
        <Ionicons name="chevron-forward" size={20} color={isDarkMode ? '#fff' : '#374151'} />
      </TouchableOpacity>

      {/* 性别选择模态框 */}
      <Modal
        visible={showGenderModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowGenderModal(false)}
      >
        <View style={{
          flex: 1,
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
          <View style={{
            padding: 16,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              选择性别
            </Text>
            
            <TouchableOpacity
              style={{
                paddingVertical: 14,
                borderBottomWidth: 1,
                borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
              }}
              onPress={() => {
                setTempData({...tempData, gender: '男'});
                setShowGenderModal(false);
              }}
            >
              <Text style={{ color: isDarkMode ? 'white' : '#1f2937' }}>男</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{
                paddingVertical: 14,
                borderBottomWidth: 1,
                borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
              }}
              onPress={() => {
                setTempData({...tempData, gender: '女'});
                setShowGenderModal(false);
              }}
            >
              <Text style={{ color: isDarkMode ? 'white' : '#1f2937' }}>女</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{ paddingVertical: 14 }}
              onPress={() => {
                setTempData({...tempData, gender: '保密'});
                setShowGenderModal(false);
              }}
            >
              <Text style={{ color: isDarkMode ? 'white' : '#1f2937' }}>保密</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{
                paddingVertical: 12,
                marginTop: 16,
                backgroundColor: isDarkMode ? '#657084' : '#e5e7eb',
                borderRadius: 8,
              }}
              onPress={() => setShowGenderModal(false)}
            >
              <Text style={{
                textAlign: 'center',
                fontWeight: '600',
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                取消
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

export default ProfileScreen; 